<template>
  <v-dialog
    v-model="requestDialog"
    width="500"
    overlay-color="rgba(39, 50, 64)"
    overlay-opacity="0.6"
    :fullscreen="$vuetify.breakpoint.xs ? true : false"
    persistent
    :transition="false"
  >
    <v-card>
      <v-card-title
        class="header info"
        primary-title
      >
        <b>{{ $t('withdrawals.withdrawButton') }}</b>
      </v-card-title>
      <v-card-text
        v-if="!me.twofa"
        class="mt-3"
      >
        {{ $t('withdrawals.enableTwoFa') }}
      </v-card-text>
      <v-card-actions v-if="!me.twofa">
        <v-spacer />
        <v-btn
          text
          outlined
          @click="$router.push('/security')"
        >
          {{ $t('security.activate') }}
        </v-btn>
      </v-card-actions>
      <v-card-text
        v-if="me.security.external_transactions_suspended && me.twofa"
        class="mt-3"
      >
        {{ $t('withdrawals.suspended') }} <b>{{ me.security.external_transactions_suspended_at | moment('L LT') }}</b>.
        {{ $t('withdrawals.suspendedWhy') }}
      </v-card-text>
      <v-card-actions v-if="me.security.external_transactions_suspended && me.twofa">
        <v-spacer />
        <v-btn
          text
          @click="$router.push('/invoices')"
        >
          {{ $t('globals.closeButton') }}
        </v-btn>
      </v-card-actions>
      <v-card-text v-if="withdrawalConfig && step == 1 && me.twofa && !me.security.external_transactions_suspended">
        <v-select
          v-model.trim="form.currency"
          class="mt-3"
          :items="withdrawalConfig"
          :item-text="'name'"
          :item-value="'name'"
          :label="$t('withdrawals.selectCurrency')"
          name="currency"
          @change="restart()"
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name" />
              <small class="text-neutral">{{ data.item.desc }}</small>
            </v-list-item-content>
          </template>
          <template
            slot="item"
            slot-scope="data"
          >
            <v-list-item-avatar class="mr-5 mt-1">
              <img :src="require('@/images/crypto/' + data.item.name.toLowerCase() + '.svg')">
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name" /><br>
              <small class="text-neutral">{{ data.item.desc }}</small>
            </v-list-item-content>
          </template>
        </v-select>
        <v-select
          v-model.trim="form.blockchain"
          class="mt-3"
          :items="blockchain"
          :item-text="'name'"
          :item-value="'key'"
          :label="$t('withdrawals.chooseBlockchain')"
          name="currency"
          @change="setBlockchainCurrency"
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name" />
              <small class="text-neutral">{{ data.item.type }}</small>
            </v-list-item-content>
          </template>
          <template
            slot="item"
            slot-scope="data"
          >
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name" />
              <small class="text-neutral">{{ data.item.type }}</small>
            </v-list-item-content>
          </template>
        </v-select>
        <v-text-field
          v-if="form.currency && form.blockchain"
          v-model.trim="form.amount"
          :value="form.amount == '' ? 0 : form.currency"
          class="mt-3"
          name="amount"
          maxlength="12"
          :label="$t('withdrawals.amount')"
          :error-messages="AmountErrors"
          @input="$v.form.amount.$touch()"
          @blur="$v.form.amount.$touch()"
          @keypress="onlyForCurrency($event, form.amount)"
        >
          <template v-slot:append>
            <small
              class="text-no-wrap text-right"
            >
              {{ $t('withdrawals.maxAllowed') }}:
              <b 
                :class="maxAllowedAmount > 0 ? 'text-positive' : ''"
                style="cursor: pointer;"
                @click="form.amount = maxAllowedAmount"
              >{{ maxAllowedAmount }} {{ form.currency }}</b>
              <br>
              <small>{{ $t('withdrawals.reqMin') }}: {{ minAmount }} {{ form.currency }}</small>
            </small>
          </template>
        </v-text-field>
        <v-text-field
          v-if="form.currency && form.blockchain"
          v-model.trim="form.wallet_address"
          class="mt-3 mb-5"
          name="wallet_address"
          :label="$t('withdrawals.wallet')"
          :error-messages="WalletErrors"
          @input="$v.form.wallet_address.$touch()"
          @blur="$v.form.wallet_address.$touch()"
          @paste="$v.form.wallet_address.$touch()"
        />
        <v-layout
          v-if="form.currency && form.blockchain"
          wrap
        >
          <v-flex xs7>
            <small>{{ $t('withdrawals.amount') }}</small>
          </v-flex>
          <v-flex
            xs5
            style="text-align:right;"
          >
            <small>{{ form.amount | numFormat(digFormat) }} {{ form.currency }}</small>
          </v-flex>
          <v-flex xs7>
            <small>{{ $t('withdrawals.fee') }}</small>
          </v-flex>
          <v-flex
            xs5
            style="text-align:right;"
          >
            <v-progress-linear
              v-if="$apollo.loading"
              color="red lighten-2"
              buffer-value="0"
              class="mt-1"
              style="width:80px;float:right;"
              height="12"
              stream
            />
            <small
              v-if="!$apollo.loading"
              :key="renderFee"
            >{{ form.amount > 0 && withdrawalFee ? withdrawalFee.gas_estimated : 0 | numFormat(digFormat) }} {{ form.currency }}</small>
          </v-flex>
          <v-flex xs7>
            <small>{{ $t('withdrawals.toWithdraw') }}</small>
          </v-flex>
          <v-flex
            xs5
            style="text-align:right;"
          >
            <b :key="renderTotal">{{ withdrawalFee && form.amount > 0 && form.amount - withdrawalFee.gas_estimated > 0 ? form.amount - withdrawalFee.gas_estimated : 0 | numFormat(digFormat) }} {{ form.currency }}</b>
          </v-flex>
          <v-checkbox
            v-model="acceptterms"
            style="margin-top:12px"
            :error-messages="AcceptTermsErrors"
            color="primary"
            @change="$v.acceptterms.$touch()"
          >
            <template v-slot:label>
              <small>{{ $t('withdrawals.iAgreee') }}</small>
            </template>
          </v-checkbox>
        </v-layout>
      </v-card-text>
      <two-fa-verification
        v-if="step == 2"
        ref="twofa"
        class="mt-5"
        method="withdrawal_request"
      />
      <v-card-text
        v-if="step == 3"
        class="mt-5 mb-5 text-center"
      >
        <helper-success-animation class="mb-4" />
        {{ $t('withdrawals.successMessage') }} <b>{{ form.wallet_address | obfuscate(5) }}</b> 
        {{ $t('withdrawals.inAmount') }} <b>{{ form.amount }} {{ form.currency }}</b> 
      </v-card-text>
      <v-divider />

      <v-card-actions v-if="me.twofa && !me.security.external_transactions_suspended">
        <v-spacer />
        <v-btn
          text
          :disabled="loading"
          @click="goBack()"
        >
          <span v-if="step == 1">{{ $t('globals.cancelButton') }}</span>
          <span v-if="step == 2">{{ $t('security.backButton') }}</span>
          <span v-if="step == 3">{{ $t('globals.closeButton') }}</span>
        </v-btn>
        <v-btn
          v-if="step < 3"
          color="primary"
          :disabled="loading || ($v.$invalid || !withdrawalFee)"
          :loading="loading"
          @click="newWithdrawal()"
        >
          {{ $t('withdrawals.withdraw') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import WITHDRAWAL_CONFIG from "@/graphql/queries/WithdrawalConfig.gql";
import REQUEST_WITHDRAWAL_MUTATION from "@/graphql/mutations/RequestWithdrawal.gql";
import WITHDRAWAL_FEE_QUERY from "@/graphql/queries/WithdrawalFee.gql";
import WALLET_BALANCES_QUERY from "@/graphql/queries/WalletBalances.gql";
import TwoFaVerification from "@/views/Security/partial/TwoFaVerification.vue"

const WAValidator = require('multicoin-address-validator');

const validateWallet = (address, vm) => {
  if (!address || ! vm.blockchain_currency) return false
  return WAValidator.validate(address, vm.blockchain_currency)
}

import { validationMixin } from "vuelidate"
import {
  required,
  minValue,
  maxValue
} from "vuelidate/lib/validators"

export default {
  name: "RequestDialog",
  components: {
    TwoFaVerification
  },
  mixins: [validationMixin],
  props: {
    withdrawalId: {
      type: String,
      default() {
        return null;
      }
    },
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      code: null,
      renderFee: 0,
      renderTotal: 0,
      step: 1,
      loading: false,
      acceptterms: false,
      form: {
        blockchain: null,
        wallet_address: null,
        currency: null,
        blockchain_currency: null,
        amount: 0
      }
    };
  },
  validations() {
    if (this.requestId == null) {
      return {
        form: this.rules,
        acceptterms: {
          checked(value) {
            return value;
          }
        }
      };
    }
    return {
      code: {
        required
      }
    };
  },
  computed: {
    withdrawalParams() {
      let params = this.form
      params.amount = parseFloat(params.amount)
      return params
    },
    maxAllowedAmount() {
      if (!this.me) return 0
      let $amount = this._.find(this.me.wallet_balance, {
        currency: this.form.currency
      });
      $amount = $amount == undefined ? 0.0 : $amount.available;
      return $amount;
    },
    blockchain() {
      if (this.form.currency == null) return null;
      return this._.find(this.withdrawalConfig, {
        name: this.form.currency
      }).blockchain;
    },
    minAmount() {
       if (this.form.currency == null) return null;
      return this._.find(this.withdrawalConfig, {
        name: this.form.currency
      }).min_amount;
    },
    digFormat() {
      if (this.form.currency == null) return null;
      return this._.find(this.withdrawalConfig, {
        name: this.form.currency
      }).format;
    },
    requestId: {
      get() {
        return this.withdrawalId;
      },
      set(val) {
        this.$parent.withdrawalId = val;
      }
    },
    requestDialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$parent.requestDialog = val;
      }
    },
    rules() {
      var form = {
        wallet_address: {
          required,
          validateWallet: validateWallet
        },
        amount: {
          required,
          minValue: minValue(this.minAmount),
          maxValue: maxValue(this.maxAllowedAmount)
        }
      };
      return form;
    },
    WalletErrors() {
      const errors = [];
      if (!this.$v.form.wallet_address.$dirty) return errors;
      !this.$v.form.wallet_address.required && errors.push(this.$t("aff.reqField"));
      !this.$v.form.wallet_address.validateWallet &&
        errors.push(this.$t("wallets.walletNotValid"));
      return errors;
    },
    AmountErrors() {
      const errors = [];
      if (!this.$v.form.amount.$dirty) return errors;
      !this.$v.form.amount.required && errors.push(this.$t("aff.reqField"));
      !this.$v.form.amount.minValue &&
        errors.push(
          this.$t("withdrawals.reqMinAmount") +
            ": " +
            this.minAmount +
            " " +
            this.form.currency
        );
      !this.$v.form.amount.maxValue &&
        errors.push(
          this.$t("withdrawals.reqMaxAmount") +
            ": " +
            this.maxAllowedAmount +
            " " +
            this.form.currency
        );
      return errors;
    },
    AcceptTermsErrors() {
      const errors = [];
      if (!this.$v.acceptterms.$dirty) return errors;
      !this.$v.acceptterms.checked &&
        errors.push(this.$t("withdrawals.acceptTerms"));
      return errors;
    },
    CodeErrors() {
      const errors = [];
      if (!this.$v.code.$dirty) return errors;
      !this.$v.code.required && errors.push(this.$t("aff.reqField"));
      return errors;
    }
  },
  watch: {
  form: {
      handler(val){
        this.$apollo.queries.withdrawalFee.refetch()
      },
      deep: true
    }
  },
  methods: {
    restart() {
      this.form.blockchain = null
      this.form.wallet_address = null
      this.form.blockchain_currency = null
      this.form.amount = 0
      this.withdrawalFee = null
      this.$v.$reset()
    },
    goBack() {
      if (this.step == 2) {
        this.step = 1
        return
      }
      this.requestDialog = false
    },
    setBlockchainCurrency() {
      this.form.blockchain_currency = this._.find(this.blockchain, {
        key: this.form.blockchain
      }).currency;
    },
    newWithdrawal() {
      switch (this.step) {
        case 1:
          this.$v.$touch()
          if (this.$v.$invalid) return
          this.step = 2
          return
        case 2:
          if (!this.$refs.twofa.isValid()) return 
          break  
      }
      this.form.amount = parseFloat(this.form.amount)
      this.form.email_verification_code = this.$refs.twofa.email_verification_code
      this.form.twofa_verification_code = this.$refs.twofa.twofa_verification_code
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: REQUEST_WITHDRAWAL_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.step = 3
          this.loading = false;
          this.$parent.$apollo.queries.withdrawals.refetch();
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false;
        });
    }
  },
  apollo: {
    withdrawalFee: {
      query: WITHDRAWAL_FEE_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return this.withdrawalParams
      },
      result(data) {
        if (!data.loading) {
          this.renderFee +=1
          this.renderTotal +=1
        }
      },
      skip() {
        return this.$v.form.$invalid
      }
    },
    withdrawalConfig: {
      query: WITHDRAWAL_CONFIG
    },
    me: {
      query: WALLET_BALANCES_QUERY
    },
  }
};
</script>

<style lang="scss" >
</style>
